import { Loader2, Search, X as CloseIcon } from 'lucide-react';
import { type ReactNode } from 'react';
import { type AddressType, type NetworkType } from '@mls-wallet/shared-types';
import { cn } from '@/lib/utils';
import type { AddressBookEntryType } from '@/types/address-book';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { AddressDisplay } from './address-display';

type AddressSelectorFilter = {
  value: string;
  label: string;
};

type AddressSelectorEntryType = AddressBookEntryType | AddressType;

type AddressSelectorDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  titleClassName?: string;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  searchPlaceholder?: string;
  filters?: AddressSelectorFilter[];
  selectedFilter?: string;
  onSelectedFilterChange?: (value: string) => void;
  contentClassName?: string;
  children: ReactNode;
};

type AddressSelectorSearchInputProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
};

type AddressSelectorResultsProps = {
  isLoading?: boolean;
  loadingMessage?: string;
  errorMessage?: string | null;
  emptyMessage?: string;
  hasResults: boolean;
  className?: string;
  children?: ReactNode;
};

type AddressSelectorRowProps = {
  address: string;
  name?: string | null;
  networkId?: NetworkType | null;
  type?: AddressSelectorEntryType | null;
  isSelected?: boolean;
  titleOverride?: string | null;
  descriptionOverride?: string | null;
  titleClassName?: string;
  descriptionClassName?: string;
  rightContent?: ReactNode;
  onClick: () => void;
  rightText?: string | null;
  className?: string;
};

export function getAddressSelectorTypeLabel(
  type?: AddressSelectorEntryType | null
): string | null {
  switch (type) {
    case 'MLSACCOUNT':
      return 'MLS Account';
    case 'CONTRACT':
      return 'Smart Contract';
    case 'EOA':
      return 'Wallet (EOA)';
    case 'UNKNOWN':
    default:
      return null;
  }
}

function AddressSelectorSearchInput({
  value,
  onChange,
  placeholder = 'Search or paste address',
  className,
}: AddressSelectorSearchInputProps) {
  return (
    <div className={cn('relative min-w-0 w-full', className)}>
      <Search className="pointer-events-none absolute left-[22px] top-1/2 h-[16px] w-[16px] -translate-y-1/2 shrink-0 text-zinc-500" />
      <Input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-[39px] min-w-0 w-full rounded-[41px] border-zinc-200 bg-white pl-[48px] pr-[22px] text-[16px] font-normal text-zinc-900 shadow-none placeholder:text-zinc-500 focus-visible:ring-0"
      />
    </div>
  );
}

export function AddressSelectorResults({
  isLoading = false,
  loadingMessage = 'Loading addresses...',
  errorMessage = null,
  emptyMessage = 'No addresses found',
  hasResults,
  className,
  children,
}: AddressSelectorResultsProps) {
  return (
    <div
      className={cn(
        'min-w-0 w-full overflow-hidden rounded-[10px] border border-solid border-zinc-200 bg-white',
        className
      )}
    >
      {/* Case: the caller is still loading results. */}
      {isLoading ? (
        <div className="flex h-[120px] items-center justify-center text-zinc-500">
          <Loader2 className="mr-2 h-6 w-6 animate-spin" />
          {loadingMessage}
        </div>
      ) : /* Case: loading finished with an error. */
      errorMessage ? (
        <div className="flex h-[120px] items-center justify-center px-6 text-center text-red-500">
          {errorMessage}
        </div>
      ) : /* Case: loading succeeded but there are no rows to show. */
      !hasResults ? (
        <div className="flex h-[120px] items-center justify-center px-6 text-center text-zinc-500">
          {emptyMessage}
        </div>
      ) : (
        /* Case: render the caller-provided result rows. */
        <div className="divide-y divide-zinc-100">{children}</div>
      )}
    </div>
  );
}

export function AddressSelectorRow({
  address,
  name = null,
  networkId = null,
  type = null,
  isSelected = false,
  titleOverride = null,
  descriptionOverride = null,
  titleClassName,
  descriptionClassName,
  rightContent,
  onClick,
  rightText = null,
  className,
}: AddressSelectorRowProps) {
  const metaText = rightText ?? getAddressSelectorTypeLabel(type);
  const hasOverrides = titleOverride !== null || descriptionOverride !== null;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isSelected}
      className={cn(
        'flex h-[72px] min-w-0 w-full justify-start gap-0 overflow-hidden whitespace-normal rounded-none border-0 px-[26px] py-[18px] text-left shadow-none outline-none transition-colors focus:outline-none focus-visible:outline-none',
        isSelected
          ? 'bg-blue-50 hover:bg-blue-50 focus-visible:bg-blue-50'
          : 'bg-white hover:bg-zinc-50 focus-visible:bg-zinc-50',
        className
      )}
    >
      <div className="flex min-w-0 flex-1 items-center justify-between gap-4 overflow-hidden">
        <div className="min-w-0 flex-1 overflow-hidden">
          {/* Case: callers can fully override the row copy for special actions. */}
          {hasOverrides ? (
            <div className="min-w-0 max-w-full space-y-0.5">
              {titleOverride ? (
                <div
                  className={cn(
                    'truncate text-sm font-medium text-foreground',
                    titleClassName
                  )}
                >
                  {titleOverride}
                </div>
              ) : null}
              {descriptionOverride ? (
                <div
                  className={cn(
                    'truncate text-xs text-muted-foreground',
                    descriptionClassName
                  )}
                >
                  {descriptionOverride}
                </div>
              ) : null}
            </div>
          ) : (
            <AddressDisplay
              address={address}
              name={name}
              networkId={networkId}
              showAddressName
            />
          )}
        </div>
        {rightContent ? (
          <div className="shrink-0">{rightContent}</div>
        ) : metaText ? (
          <span className="shrink-0 text-[13px] text-zinc-500">{metaText}</span>
        ) : null}
      </div>
    </button>
  );
}

export function AddressSelectorDialog({
  open,
  onOpenChange,
  title,
  description,
  titleClassName,
  searchQuery,
  onSearchQueryChange,
  searchPlaceholder = 'Search or paste address',
  filters = [],
  selectedFilter,
  onSelectedFilterChange,
  contentClassName,
  children,
}: AddressSelectorDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          '!flex !flex-col gap-0 overflow-hidden p-0 [&>button]:hidden sm:max-w-[609px]',
          contentClassName
        )}
      >
        <div className="relative flex min-w-0 flex-col gap-[22px] p-[20px]">
          <div className="flex items-center justify-between px-[12px]">
            <DialogTitle
              className={cn(
                'text-[16px] font-semibold text-zinc-600',
                titleClassName
              )}
            >
              {title}
            </DialogTitle>
          </div>

          {description ? (
            <DialogDescription className="min-w-0 px-[12px] text-base text-zinc-700">
              {description}
            </DialogDescription>
          ) : null}

          <AddressSelectorSearchInput
            value={searchQuery}
            onChange={onSearchQueryChange}
            placeholder={searchPlaceholder}
          />

          {/* Case: some callers need top-level tabs for address-type filtering. */}
          {filters.length > 0 ? (
            <div className="-mx-1 overflow-x-auto px-1">
              <div className="inline-flex h-[46px] min-w-max items-center rounded-full bg-zinc-100 p-[4px]">
                {filters.map((filter) => (
                  <Button
                    key={filter.value}
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onSelectedFilterChange?.(filter.value)}
                    className={cn(
                      'h-[38px] rounded-full px-5 text-[14px] font-medium leading-none transition-colors focus-visible:border-transparent focus-visible:ring-0 focus-visible:outline-none',
                      selectedFilter === filter.value
                        ? 'bg-white text-zinc-900 shadow-sm'
                        : 'text-zinc-600 hover:text-zinc-900'
                    )}
                  >
                    {filter.label}
                  </Button>
                ))}
              </div>
            </div>
          ) : null}

          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-[20px] top-[8px] h-auto w-auto rounded-[58px] bg-transparent p-[15px] transition-colors hover:bg-zinc-100"
            onClick={() => onOpenChange(false)}
          >
            <CloseIcon className="h-[18px] w-[18px]" />
            <span className="sr-only">Close</span>
          </Button>
        </div>

        {children}
      </DialogContent>
    </Dialog>
  );
}
